<template>
  <q-btn
    icon="help"
    color="info"
    size="md"
    dense
    :text-color="$isColorDark(currentSite.infoColor) ? 'white' : 'black'"
  >
    <q-tooltip>Support Resources</q-tooltip>
    <q-menu
      bordered
      side="right"
      overlay
      class="support-menu"
      style="width: 400px; min-width: 400px"
    >
      <q-toolbar
        class="bg-info"
        :class="$isColorDark(currentSite.infoColor) ? 'text-white' : 'text-black'"
      >
        <q-toolbar-title>Support Resources</q-toolbar-title>
      </q-toolbar>

      <div
        v-if="items.length === 0"
        class="q-pa-md text-center text-grey"
      >
        No support resources available for this section.
      </div>

      <q-scroll-area style="height: 400px; max-height: 65vh">
        <q-list padding>
          <q-item
            v-for="supportItem in items"
            :key="supportItem.id"
            v-ripple
            clickable
            class="support-item q-my-sm"
            @click="openItem(supportItem)"
          >
            <q-item-section avatar>
              <q-avatar rounded>
                <img
                  v-if="supportItem.thumbnailUrl"
                  :src="supportItem.thumbnailUrl"
                >
                <img
                  v-else-if="supportItem.video || supportItem.youtubeSlug"
                  :src="'https://img.youtube.com/vi/' + (supportItem.video || supportItem.youtubeSlug) + '/0.jpg'"
                >
                <q-icon
                  v-else
                  :name="supportItem.type === 'Video' ? 'ondemand_video' : 'article'"
                  size="md"
                  color="primary"
                />
              </q-avatar>
            </q-item-section>

            <q-item-section>
              <q-item-label class="text-weight-medium">
                {{ supportItem.title }}
              </q-item-label>
              <q-item-label
                v-if="supportItem.description"
                caption
              >
                {{ supportItem.description }}
              </q-item-label>
              <q-item-label
                v-else
                caption
              >
                {{ supportItem.type === 'Video' ? 'Video tutorial' : 'Documentation article' }}
              </q-item-label>
            </q-item-section>

            <q-item-section side>
              <q-icon
                :name="supportItem.type === 'Video' ? 'play_circle' : 'open_in_new'"
                color="info"
              />
            </q-item-section>
          </q-item>
        </q-list>
      </q-scroll-area>

      <q-separator />
      <q-item
        v-ripple
        clickable
        class="text-primary"
        @click="openSupportArticles"
      >
        <q-item-section>View All Support Articles</q-item-section>
        <q-item-section avatar>
          <q-icon name="arrow_forward" />
        </q-item-section>
      </q-item>
    </q-menu>

    <q-dialog
      v-model="showFullScreenVideo"
      full-width
    >
      <q-card>
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">
            {{ currentItem?.title }}
          </div>
          <q-space />
          <q-btn
            v-close-popup
            icon="close"
            flat
            round
            dense
          />
        </q-card-section>

        <q-card-section>
          <q-video
            style="width: 100%"
            :ratio="16/9"
            :src="currentVideoUrl"
          />
        </q-card-section>
      </q-card>
    </q-dialog>

    <q-dialog
      v-model="showFullScreenArticle"
      full-width
      :maximized="true"
      transition-show="fade-in"
      transition-hide="fade-out"
      content-class="article-fullscreen-dialog"
    >
      <q-card class="article-card">
        <q-card-section
          class="article-header"
        >
          <div class="text-h6">
            {{ currentItem?.title }}
          </div>
          <q-space />
          <q-btn
            v-close-popup
            icon="close"
            flat
            round
            dense
          />
        </q-card-section>

        <q-card-section class="article-content">
          <div class="article-wrapper">
            <markdown-content
              :content="currentItem?.content"
              :enable-sticky-headers="true"
            />
          </div>
        </q-card-section>
      </q-card>
    </q-dialog>
  </q-btn>
</template>

<script>
import { adminRoot } from 'src/config'
import MarkdownContent from 'src/modules/admin/components/MarkdownContent.vue'
import { categories } from 'src/modules/admin/support/Support'

export default {
  name: 'SupportMenu',
  components: {
    MarkdownContent
  },
  configuration: ['currentSite'],
  props: {
    category: {
      type: String,
      required: false,
      default: null
    },
    video: {
      type: String,
      required: false,
      default: null
    },
    article: {
      type: String,
      required: false,
      default: null
    }
  },
  data () {
    return {
      showFullScreenVideo: false,
      showFullScreenArticle: false,
      youtubeSlug: null,
      currentItem: null
    }
  },
  computed: {
    currentVideoUrl () {
      if (this.currentItem && this.currentItem.videoUrl) {
        return this.currentItem.videoUrl
      }
      return 'https://www.youtube.com/embed/' + this.youtubeSlug
    },
    items () {
      // Check if we have support articles from the model
      if (this.supportArticles && this.supportArticles.data && this.supportArticles.data.length > 0) {
        if (this.category) {
          // Filter articles by category name
          return _.filter(this.supportArticles.data, article =>
            article.published && article.supportCategory && article.supportCategory.name === this.category)
        } else if (this.article) {
          // Find specific article by title
          return _.filter(this.supportArticles.data, article =>
            article.published && article.title === this.article)
        } else if (this.video) {
          // Find specific video by title
          return _.filter(this.supportArticles.data, article =>
            article.published && article.type === 'Video' && article.title === this.video)
        } else {
          // Return all published articles
          return _.filter(this.supportArticles.data, 'published')
        }
      } else {
        // Fallback to old video system
        if (this.category) {
          const categoryData = _.find(categories, { title: this.category })
          return categoryData ? categoryData.videos : []
        } else if (this.video) {
          return [_(categories).map('videos').flatten().find({ title: this.video })]
        } else {
          throw new Error('Must provide category or video/article name')
        }
      }
    }
  },
  models: {
    supportArticles: {
      supportCategory: 'default',
      variables: {
        limit: 200
      }
    }
  },
  methods: {
    openItem (item) {
      this.currentItem = item

      if (item.type === 'Video' || item.youtubeSlug) {
        // Handle video content
        this.showFullScreenVideo = true
        this.youtubeSlug = item.video || item.youtubeSlug
      } else {
        // Handle article content
        this.showFullScreenArticle = true
      }
    },
    playVideo (slug) {
      this.showFullScreenVideo = true
      this.youtubeSlug = slug
    },
    openSupportArticles () {
      this.$router.push(adminRoot + 'support')
    }
  }
}
</script>

<style lang="scss" scoped>
.smooth-scroll {
  scroll-behavior: smooth;
}

/* Article dialog styling */
:deep(.article-fullscreen-dialog) {
  /* Override any Quasar dialog styles that might interfere */
  overflow: hidden !important;
}

.article-card {
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100%;
  max-width: 100%;
  max-height: 100vh;
  margin: 0;
  padding: 0;
  overflow: hidden;
}

.article-header {
  position: sticky;
  top: 0;
  z-index: 100;
  background-color: white;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 12px 16px !important;
}

.article-content {
  flex: 1;
  overflow-y: auto;
  height: calc(100vh - 60px);
  padding: 0 !important;
}

.article-wrapper {
  padding: 24px;
  margin: 0;
  background: white;
}

/* Override any Quasar styles that might interfere */
:deep(.q-dialog__inner--maximized > div) {
  max-width: 100%;
  max-height: 100%;
}
</style>
